'use client';

import { useUserEventLogger } from '@repo/tracking-service';
import { useAtom, useSetAtom } from 'jotai';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import s from '@/app/_components/chat-content/ChatTextarea.module.scss';
import { useChatMessage } from '@/lib/hooks/use-chat-message';
import { currentQuestionAtom, questionTextareaValueAtom } from '@/lib/stores/chat';

const ROLLING_GUIDE_TEXTS = ['AI 답변이 완벽하지 않을 수 있으니 확인 후 활용하세요', '개인이나 단체를 알아볼 수 있는 질문은 피해주세요'] as const;
const ROLLING_DELAY_MS = 5000;
const ROLLING_FADE_OUT_MS = 200;
const ROLLING_FADE_IN_MS = 600;

export const ChatTextarea = ({ location }: { location: 'top' | 'bottom' }) => {
  const router = useRouter();
  const pathname = usePathname();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [textareaValue, setTextareaValue] = useAtom(questionTextareaValueAtom);
  const setCurrentQuestion = useSetAtom(currentQuestionAtom);
  const isComposingRef = useRef(false);
  const [state, setState] = useState<'disabled' | 'active' | 'loading'>('disabled');
  const { sendClickEvent, sendRequestEvent } = useUserEventLogger();
  const sendButtonIcon = {
    disabled: `ic_arrow-up_gy_rg.svg`,
    active: `ic_arrow-up_wh_rg.svg`,
    loading: `ic_stop_wh.svg`
  };
  const [rollingTextIndex, setRollingTextIndex] = useState(0);
  const [isRollingFadeOut, setIsRollingFadeOut] = useState(false);

  const { abortMessage, isLoading, isStreaming, generateNewChatId, sendMessage } = useChatMessage();

  const handleSubmit = () => {
    if (state === 'loading') {
      abortMessage();
    } else if (textareaRef.current) {
      if (!textareaValue.trim()) return;
      setCurrentQuestion(textareaValue);
      sendRequestEvent('chat-input', { location });
      if (pathname === '/chat') {
        sendMessage(textareaValue);
      } else {
        generateNewChatId();
        router.push(`/chat`);
      }
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      if (textareaRef.current.scrollHeight > 0) textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;

      setState(textareaRef.current.value.trim() ? 'active' : 'disabled');
    }
  }, [setTextareaValue, textareaValue]);

  useEffect(() => {
    if (isLoading || isStreaming) {
      setState('loading');
    } else if (!isLoading && !isStreaming && !textareaValue.trim()) {
      setState('disabled');
    }
  }, [isLoading, isStreaming, textareaValue]);

  useEffect(() => {
    setTextareaValue('');
  }, [pathname, setTextareaValue]);

  useEffect(() => {
    let isCancelled = false;
    let delayTimer: ReturnType<typeof setTimeout> | undefined;
    let fadeOutTimer: ReturnType<typeof setTimeout> | undefined;

    const scheduleNext = () => {
      delayTimer = setTimeout(() => {
        if (isCancelled) return;
        setIsRollingFadeOut(true);

        fadeOutTimer = setTimeout(() => {
          if (isCancelled) return;
          setRollingTextIndex((prev) => (prev + 1) % ROLLING_GUIDE_TEXTS.length);
          setIsRollingFadeOut(false);
          scheduleNext();
        }, ROLLING_FADE_OUT_MS * 0.8);
      }, ROLLING_DELAY_MS);
    };

    scheduleNext();

    return () => {
      isCancelled = true;
      if (delayTimer) clearTimeout(delayTimer);
      if (fadeOutTimer) clearTimeout(fadeOutTimer);
    };
  }, []);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      <div
        className={'pl-normal-large pr-normal-small py-tiny-large mb-normal-small bg-neutral-background-lowest rounded-[28px] flex items-end gap-tiny-large'}
      >
        <textarea
          ref={textareaRef}
          className={
            'flex-1 border-none outline-none py-tiny-large pr-tiny-large placeholder:text-neutral-foreground-lowest !bg-neutral-background-lowest body-medium-medium resize-none h-auto max-h-[25svh]'
          }
          rows={1}
          disabled={isLoading || isStreaming}
          value={textareaValue}
          onChange={(event) => setTextareaValue(event.target.value)}
          onKeyUp={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (isComposingRef.current) {
                return;
              }
              handleSubmit();
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              isComposingRef.current = event.nativeEvent.isComposing;
              event.preventDefault();
            }
          }}
          placeholder={state === 'loading' ? '세나가 답변을 작성하고 있어요' : '세나에게 질문해보세요'}
        />
        <div className={'py-[3px]'}>
          <button
            title={state === 'loading' ? '취소하기' : '전송하기'}
            type={'button'}
            onClick={() => {
              sendClickEvent(state === 'loading' ? 'chat-input_stop' : 'chat-input_request', { location });
              handleSubmit();
            }}
            disabled={state === 'disabled'}
            className={`!bg-neutral-background-disabled-low ${s.chat_button} ${state === 'loading' ? s.loading : state === 'active' ? s.active : '!cursor-auto'}`}
          >
            <div className={s.active_background} />
            <div className={s.loading_background} />
            <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/icons/${sendButtonIcon[state]}`} className={'z-10'} alt="send button" />
          </button>
        </div>
      </div>
      <div className={'text-center caption-xsmall-regular text-neutral-foreground-lowest pb-tiny-large'}>
        <span
          className="inline-block transition-opacity ease-in-out"
          style={{
            opacity: isRollingFadeOut ? 0 : 1,
            transitionDuration: isRollingFadeOut ? `${ROLLING_FADE_OUT_MS}ms` : `${ROLLING_FADE_IN_MS}ms`
          }}
        >
          {ROLLING_GUIDE_TEXTS[rollingTextIndex]}
        </span>
      </div>
    </form>
  );
};
